import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { submitContactForm, getSettings } from '../services/api';
import { Phone, Mail, MapPin } from 'lucide-react';
import SEO from '../components/SEO';
import { trackEvent } from '../lib/ga';

const Contact = () => {
  const [contactType, setContactType] = useState('customer');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    smsConsent: false,
    smsMarketingConsent: false,
    bestContactMethod: 'email',
    message: '',
    // Vendor fields
    companyName: '',
    companyPhone: '',
    websiteUrl: '',
    position: ''
  });
  const [contactInfo, setContactInfo] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState(''); // Honeypot for spam bots
  const [formLoadTime] = useState(Date.now()); // Track form load time

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getSettings();
        setContactInfo(settings);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Validation functions
  const containsHtml = (str) => {
    const htmlRegex = /<[^>]*>/g;
    return htmlRegex.test(str);
  };

  const isFakePhoneNumber = (phone) => {
    if (!phone) return false;
    // Remove all non-digits
    const digitsOnly = phone.replace(/\D/g, '');
    // Check for 555 area code (common fake number)
    if (digitsOnly.startsWith('555') || digitsOnly.includes('555')) {
      // 555-0100 through 555-0199 are reserved for fiction
      const areaCheck = digitsOnly.substring(0, 3);
      if (areaCheck === '555') return true;
    }
    // Check if phone has 555 as area code after country code
    if (digitsOnly.length >= 10) {
      const possibleAreaCode = digitsOnly.substring(digitsOnly.length - 10, digitsOnly.length - 7);
      if (possibleAreaCode === '555') return true;
    }
    return false;
  };

  const isValidPhone = (phone) => {
    if (!phone) return true; // Optional field for customers
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
  };

  const isValidUrl = (url) => {
    if (!url) return true;
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    // Check honeypot (should be empty)
    if (honeypot) {
      console.log('Bot detected via honeypot');
      return { valid: false, error: 'Spam detected' };
    }

    // Check if form was submitted too quickly (less than 3 seconds)
    if (Date.now() - formLoadTime < 3000) {
      return { valid: false, error: 'Please take your time filling out the form' };
    }

    // Check for HTML in all text fields
    const textFields = ['name', 'email', 'message', 'companyName', 'position'];
    for (const field of textFields) {
      if (containsHtml(formData[field])) {
        return { valid: false, error: 'HTML is not allowed in form fields' };
      }
    }

    // Validate email
    if (!isValidEmail(formData.email)) {
      return { valid: false, error: 'Please enter a valid email address' };
    }

    // Check for fake phone numbers
    if (isFakePhoneNumber(formData.phone)) {
      return { valid: false, error: 'Please enter a valid phone number (555 numbers are not accepted)' };
    }

    // Validate phone format if provided
    if (formData.phone && !isValidPhone(formData.phone)) {
      return { valid: false, error: 'Please enter a valid phone number' };
    }

    // If text message is preferred, phone and SMS consent are required
    if (formData.bestContactMethod === 'text') {
      if (!formData.phone || !formData.phone.trim()) {
        return { valid: false, error: 'Phone number is required when selecting text message as contact method' };
      }
      if (!formData.smsConsent) {
        return { valid: false, error: 'SMS consent is required when selecting text message as contact method' };
      }
    }

    // Vendor-specific validations
    if (contactType === 'vendor') {
      if (!formData.companyName.trim()) {
        return { valid: false, error: 'Company name is required for vendors' };
      }
      if (!formData.companyPhone.trim()) {
        return { valid: false, error: 'Company phone is required for vendors' };
      }
      if (isFakePhoneNumber(formData.companyPhone)) {
        return { valid: false, error: 'Please enter a valid company phone number (555 numbers are not accepted)' };
      }
      if (!isValidPhone(formData.companyPhone)) {
        return { valid: false, error: 'Please enter a valid company phone number' };
      }
      if (!formData.websiteUrl.trim()) {
        return { valid: false, error: 'Website URL is required for vendors' };
      }
      if (!isValidUrl(formData.websiteUrl)) {
        return { valid: false, error: 'Please enter a valid website URL' };
      }
      if (!formData.position.trim()) {
        return { valid: false, error: 'Position in company is required for vendors' };
      }
    }

    return { valid: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateForm();
    if (!validation.valid) {
      toast({
        title: 'Validation Error',
        description: validation.error,
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    
    try {
      const submitData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        sms_consent: formData.smsConsent,
        sms_marketing_consent: formData.smsMarketingConsent,
        best_contact_method: formData.bestContactMethod,
        message: formData.message,
        entry_type: contactType,
        ...(contactType === 'vendor' && {
          company_name: formData.companyName,
          company_phone: formData.companyPhone,
          website_url: formData.websiteUrl,
          position: formData.position
        })
      };

      await submitContactForm(submitData);
      toast({
        title: 'Message Sent!',
        description: 'Thank you for reaching out. We\'ll get back to you soon!'
      });
      setFormData({ 
        name: '', 
        email: '', 
        phone: '',
        smsConsent: false,
        smsMarketingConsent: false,
        bestContactMethod: 'email',
        message: '',
        companyName: '',
        companyPhone: '',
        websiteUrl: '',
        position: ''
      });
      setContactType('customer');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div 
      className="min-h-screen" 
      style={{ 
        fontFamily: 'Outfit, sans-serif',
        backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
      }}
    >
      <SEO 
        title="Contact Us"
        description="Contact 1st Ave ICP ice cream shop in Mineral Wells, TX. Call 817-829-4878 or visit us at 103 NE 1st Ave. Open Tue-Sun. Get directions, hours, and send us a message!"
        keywords="contact ice cream shop Mineral Wells, 1st Ave ICP phone number, ice cream Mineral Wells TX address, directions to ice cream shop"
        canonical="/contact"
      />
      {/* Hero Section */}
      <section className="pt-28 lg:pt-32 pb-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-black mb-4"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            Contact Us
          </h1>
          <div className="flex items-center justify-center gap-2 text-base">
            <Link to="/" className="text-pink-600 hover:underline">🏠 Home</Link>
            <span className="text-pink-600">»</span>
            <span className="text-pink-600">Contact Us</span>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8 items-start">
            {/* Left Side - Contact Cards */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500 rounded-full mb-4">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-black mb-2" style={{ fontFamily: 'Rowdies, cursive' }}>Call Now</h3>
                <a
                  href={`tel:${contactInfo.phone || '(817) 829-4878'}`}
                  onClick={() => trackEvent('phone_click', { source: 'contact_page' })}
                  className="text-gray-700 hover:text-teal-600 transition-colors"
                >
                  {contactInfo.phone || '(817) 829-4878'}
                </a>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500 rounded-full mb-4">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-black mb-2" style={{ fontFamily: 'Rowdies, cursive' }}>Email Now</h3>
                <a
                  href={`mailto:${contactInfo.email || '1staveicp@gmail.com'}`}
                  className="text-gray-700 hover:text-teal-600 transition-colors break-all text-sm"
                >
                  {contactInfo.email || '1staveicp@gmail.com'}
                </a>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-500 rounded-full mb-4">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-black mb-2" style={{ fontFamily: 'Rowdies, cursive' }}>Address</h3>
                <p className="text-gray-700 text-sm">
                  {contactInfo.address || '103 NE 1st Avenue Mineral Wells, TX 76067'}
                </p>
              </div>
            </div>

            {/* Right Side - Contact Form */}
            <div className="lg:col-span-2 bg-teal-500 p-8 lg:p-10 rounded-3xl shadow-xl">
              <div className="flex items-center gap-4 mb-8">
                <div className="bg-white p-3 lg:p-4 rounded-2xl flex-shrink-0">
                  <img
                    src="/images/icons/logo-small.png"
                    alt="Ice cream icon"
                    className="w-10 h-10 lg:w-12 lg:h-12 object-contain"
                  />
                </div>
                <h2 className="text-2xl lg:text-4xl font-black text-white" style={{ fontFamily: 'Rowdies, cursive' }}>We'd Love to Hear From You</h2>
              </div>
              <div className="border-t-2 border-dotted border-white/30 mb-8"></div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from users, bots will fill it */}
                <input
                  type="text"
                  name="website_url_confirm"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                  tabIndex="-1"
                  autoComplete="off"
                />

                {/* Customer/Vendor Selection */}
                <div className="bg-white/10 p-4 rounded-2xl">
                  <label className="block text-white font-bold mb-3">I am a:</label>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="contactType"
                        value="customer"
                        checked={contactType === 'customer'}
                        onChange={(e) => setContactType(e.target.value)}
                        className="w-5 h-5 accent-pink-500"
                      />
                      <span className="text-white font-semibold">Customer</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="contactType"
                        value="vendor"
                        checked={contactType === 'vendor'}
                        onChange={(e) => setContactType(e.target.value)}
                        className="w-5 h-5 accent-pink-500"
                      />
                      <span className="text-white font-semibold">Vendor</span>
                    </label>
                  </div>
                </div>

                {/* Basic Fields */}
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-6 py-4 rounded-2xl border-2 border-white focus:border-pink-500 focus:outline-none text-lg"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-6 py-4 rounded-2xl border-2 border-white focus:border-pink-500 focus:outline-none text-lg"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder={contactType === 'vendor' ? "Your Direct Phone" : "Phone (optional)"}
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-6 py-4 rounded-2xl border-2 border-white focus:border-pink-500 focus:outline-none text-lg"
                />

                {/* Preferred Contact Method - for both customer and vendor */}
                <div className="bg-white/10 p-4 rounded-2xl">
                  <label className="block text-white font-bold mb-3">Preferred Method of Contact:</label>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="bestContactMethod"
                        value="email"
                        checked={formData.bestContactMethod === 'email'}
                        onChange={handleChange}
                        className="w-5 h-5 accent-pink-500"
                      />
                      <span className="text-white font-semibold">📧 Email</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="bestContactMethod"
                        value="phone"
                        checked={formData.bestContactMethod === 'phone'}
                        onChange={handleChange}
                        className="w-5 h-5 accent-pink-500"
                      />
                      <span className="text-white font-semibold">📞 Phone Call</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="bestContactMethod"
                        value="text"
                        checked={formData.bestContactMethod === 'text'}
                        onChange={handleChange}
                        className="w-5 h-5 accent-pink-500"
                      />
                      <span className="text-white font-semibold">💬 Text Message</span>
                    </label>
                  </div>
                </div>

                {/* SMS Consent - Only show when Text Message is selected */}
                {formData.bestContactMethod === 'text' && (
                  <div className="bg-yellow-50 p-4 rounded-2xl border-2 border-yellow-300">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.smsConsent}
                        onChange={(e) => setFormData({...formData, smsConsent: e.target.checked})}
                        className="w-5 h-5 mt-1 accent-pink-500"
                        required
                      />
                      <span className="text-sm text-gray-700">
                        <strong className="text-yellow-700">Required for text:</strong> I agree to receive text messages from 1st Ave ICP. Message frequency varies. Message & data rates may apply. Reply STOP to unsubscribe.
                      </span>
                    </label>
                  </div>
                )}

                {/* SMS Marketing Opt-In - Always visible */}
                <div className="bg-blue-50 p-4 rounded-2xl border-2 border-blue-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.smsMarketingConsent}
                      onChange={(e) => setFormData({...formData, smsMarketingConsent: e.target.checked})}
                      className="w-5 h-5 mt-1 accent-pink-500"
                    />
                    <span className="text-sm text-gray-700">
                      <strong className="text-blue-700">📱 Yes, I want to receive SMS/text messages</strong> from 1ST Avenue ICP about specials, promotions, and updates. Message frequency varies (approx. 2-4/month). Message & data rates may apply. Reply STOP to unsubscribe. View our{' '}
                      <Link to="/privacy-policy" className="text-pink-600 underline hover:text-pink-800">Privacy Policy</Link>
                      {' '}and{' '}
                      <Link to="/sms-terms" className="text-pink-600 underline hover:text-pink-800">SMS Terms</Link>.
                    </span>
                  </label>
                </div>

                {/* Vendor-specific fields */}
                {contactType === 'vendor' && (
                  <div className="space-y-6 bg-white/10 p-6 rounded-2xl">
                    <h3 className="text-white font-bold text-lg mb-4">Vendor Information</h3>
                    <input
                      type="text"
                      name="companyName"
                      placeholder="Company Name *"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 rounded-2xl border-2 border-white focus:border-pink-500 focus:outline-none text-lg"
                    />
                    <input
                      type="tel"
                      name="companyPhone"
                      placeholder="Company Phone *"
                      value={formData.companyPhone}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 rounded-2xl border-2 border-white focus:border-pink-500 focus:outline-none text-lg"
                    />
                    <input
                      type="text"
                      name="websiteUrl"
                      placeholder="Website URL *"
                      value={formData.websiteUrl}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 rounded-2xl border-2 border-white focus:border-pink-500 focus:outline-none text-lg"
                    />
                    <input
                      type="text"
                      name="position"
                      placeholder="Your Position in Company *"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 rounded-2xl border-2 border-white focus:border-pink-500 focus:outline-none text-lg"
                    />
                  </div>
                )}

                <textarea
                  name="message"
                  placeholder="Your Message *"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-6 py-4 rounded-2xl border-2 border-white focus:border-pink-500 focus:outline-none text-lg"
                ></textarea>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-bold text-xl px-12 py-6 rounded-full shadow-lg w-full disabled:opacity-50"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="bg-teal-500 p-8 rounded-3xl shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3355.8!2d-98.1123!3d32.8087!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDQ4JzMxLjMiTiA5OMKwMDYnNDQuMyJX!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="500"
              style={{ border: 0, borderRadius: '1rem' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Location Map"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
